import React, { useEffect, useState } from "react";
import { Link2, Clock, Loader2, CheckCircle2, AlertTriangle, Instagram, Music2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ConnectedAccount } from "@/components/social/ConnectedAccount";
import { useAuth } from "@/context/AuthContext";
import { useProjects } from "@/context/ProjectContext";
import { useLang } from "@/context/LangContext";

const ICONS = { instagram: Instagram, tiktok: Music2 };
const EN_NAMES = { instagram: "Instagram", tiktok: "TikTok", snapchat: "Snapchat", x: "X (Twitter)" };
const DAYS_EN = { "الأحد": "Sun", "الاثنين": "Mon", "الإثنين": "Mon", "الثلاثاء": "Tue", "الأربعاء": "Wed", "الخميس": "Thu", "الجمعة": "Fri", "السبت": "Sat" };
const daysEn = (s) => String(s || "").split(/\s*[–\-،,]\s*/).map((d) => DAYS_EN[d.trim()] || d.trim()).join(" – ");

export const AccountCards = ({ onChange }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { t, lang } = useLang();
  const pname = (p) => (lang === "en" ? EN_NAMES[p.id] || p.name : p.name);
  const [providers, setProviders] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(null);

  const { activeProject } = useProjects();
  const pid = activeProject?.id || "";
  const load = () => Promise.all([api.get("/social/providers"), api.get(`/social/accounts${pid ? `?project_id=${pid}` : ""}`)])
    .then(([p, a]) => { setProviders(p.data); setAccounts(a.data); onChange?.(a.data); }).catch(() => {});
  useEffect(() => { load(); }, [pid]); // eslint-disable-line

  const connect = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await api.get(`/social/connect/${p.id}${pid ? `?project_id=${pid}` : ""}`);
      // داخل إطار المعاينة ترفض إنستغرام العرض (refused to connect) — نفتح الربط في النافذة العليا
      if (window.self !== window.top) { window.open(data.url, "_blank", "noopener"); setBusy(null); toast.info("أكمل الربط في النافذة الجديدة ثم عد هنا — تتحدّث الحالة تلقائياً"); }
      else window.location.assign(data.url);
    }
    catch (e) { toast.info(formatError(e.response?.data?.detail)); setBusy(null); }
  };
  useEffect(() => {
    // عند العودة من إنستغرام (أو من ذاكرة الرجوع في سفاري) نعيد تحميل الحسابات ونوقف مؤشر الانتظار
    const back = () => { setBusy(null); load(); };
    window.addEventListener("focus", back); window.addEventListener("pageshow", back);
    return () => { window.removeEventListener("focus", back); window.removeEventListener("pageshow", back); };
  }, []); // eslint-disable-line
  const disconnect = async (acc) => {
    if (!window.confirm(t("فصل هذا الحساب؟ ستتوقف الجدولة التلقائية عليه.", "Disconnect this account? Automatic scheduling on it will stop."))) return;
    try { await api.delete(`/social/accounts/${acc.id}`); toast.success(t("تم فصل الحساب", "Account disconnected")); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="glass rounded-2xl p-5 border border-border" data-testid="social-accounts">
      <div className="flex items-center gap-2 mb-1"><Link2 className="w-5 h-5 text-primary" /><h2 className="font-bold">{t("حساباتك على المنصات", "Your social accounts")}</h2></div>
      <p className="text-xs text-muted-foreground mb-2">{t("ربط رسمي وآمن عبر صفحة تسجيل الدخول الخاصة بكل منصة — رواج الذكية لا تطلب ولا تخزّن كلمة مرورك أبداً.", "Official, secure connection through each platform's own login page — Rawaj Smart never asks for or stores your password.")} {t(`أوقات النشر أدناه ${providers[0]?.best_times_source || "وفق أفضل الممارسات العالمية"}.`, "Posting times below follow global best practices.")}</p>
      {providers.length > 0 && (
        <div className="text-xs rounded-xl border border-primary/30 bg-primary/5 px-3 py-2 mb-4 flex items-start gap-2" data-testid="channels-availability-note">
          <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
          <span>
            <b>{t("المتاح الآن:", "Available now:")} {providers.filter((p) => p.configured).map(pname).join(t("، ", ", ")) || "—"}</b> {t("(ربط ونشر تلقائي ومتابعة أداء).", "(connect, auto-publish and performance tracking).")}
            {" "}{providers.filter((p) => !p.configured).map(pname).join(t("، ", ", "))} {t("تُضاف لاحقاً — وكل قناة نفعّلها تظهر هنا تلقائياً دون أي خطوة منك.", "coming soon — each channel we enable appears here automatically.")}
          </span>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {providers.map((p) => {
          const acc = accounts.find((a) => a.platform === p.id);
          const Icon = ICONS[p.id] || Link2;
          return (
            <div key={p.id} className="border border-border rounded-xl p-4 flex flex-col gap-3" data-testid={`provider-${p.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 font-bold text-sm"><Icon className="w-4 h-4 text-primary" /> {pname(p)}</div>
                {acc ? <span className={`text-[11px] px-2 py-0.5 rounded-full flex items-center gap-1 ${acc.status === "reauth" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-700"}`} data-testid={`account-status-${acc.platform}`}><CheckCircle2 className="w-3 h-3" /> {acc.status === "reauth" ? t("انتهت الصلاحية — أعد الربط", "Expired — reconnect") : t("مرتبط", "Connected")}</span>
                  : !p.configured ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground flex items-center gap-1" data-testid={`provider-pending-${p.id}`}><Clock className="w-3 h-3" /> {t("متاح لاحقاً", "Coming soon")}</span>
                  : <span className="text-[11px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{t("غير مرتبط", "Not connected")}</span>}
              </div>
              {acc ? (
                <ConnectedAccount acc={acc} onDisconnect={() => disconnect(acc)} onSynced={load} />
              ) : (
                <>
                  <div className="text-xs text-muted-foreground" title={p.best_times_source}>
                    <div>{t("أيام العمل:", "Weekdays:")} {p.best_times.weekday.join(" · ")}</div>
                    <div>{t("الجمعة والسبت:", "Fri & Sat:")} {p.best_times.weekend.join(" · ")}</div>
                    <div className="text-[11px] text-primary">{t("أفضل الأيام:", "Best days:")} {lang === "en" ? daysEn(p.best_times.top_days) : p.best_times.top_days}</div>
                  </div>
                  {!p.configured && <div className="text-[11px] text-muted-foreground flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0 text-accent" /> {t("هذه القناة تُضاف لاحقاً — ستظهر مفعّلة هنا تلقائياً فور إتاحتها.", "This channel is coming soon — it will appear enabled here automatically.")}</div>}
                  {isAdmin && <details className="text-[10px] text-muted-foreground" data-testid={`redirect-uris-${p.id}`}><summary className="cursor-pointer">روابط العودة (للمشرف)</summary>{(p.redirect_uris || []).map((u) => <div key={u} dir="ltr" className="break-all select-all">{u}</div>)}</details>}
                  <Button size="sm" className="gap-1 mt-auto" disabled={!p.configured || busy === p.id} onClick={() => connect(p)} data-testid={`connect-${p.id}`}>
                    {busy === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />} {t("ربط الحساب", "Connect account")}
                  </Button>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
